import React, { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { useToast } from "../hooks/use-toast";
import { usersAPI } from "../services/api";
import { KeyRound, Eye, EyeOff, Loader2 } from "lucide-react";

export function ChangePasswordModal({ children }) {
  const { toast } = useToast();
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const [formData, setFormData] = useState({
    current_password: "",
    new_password: "",
    confirm_password: "",
  });

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.new_password.trim()) {
      toast({
        title: "Atenção",
        description: "A senha não pode ficar em branco.",
        variant: "destructive",
      });
      return;
    }

    if (formData.new_password !== formData.confirm_password) {
      toast({
        title: "Erro",
        description: "As novas senhas não coincidem.",
        variant: "destructive",
      });
      return;
    }

    setLoading(true);
    try {
      await usersAPI.changeMyPassword({
        current_password: formData.current_password,
        new_password: formData.new_password,
      });

      toast({
        title: "Sucesso",
        description: "Sua senha foi alterada com sucesso!",
      });
      setIsOpen(false);
      setFormData({
        current_password: "",
        new_password: "",
        confirm_password: "",
      });
    } catch (error) {
      toast({
        title: "Erro",
        description: error.response?.data?.detail || "Erro ao alterar a senha.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>{children}</DialogTrigger>
      <DialogContent className="w-full max-w-[95vw] sm:max-w-md max-h-[95dvh] p-4 sm:p-6 overflow-y-auto overflow-x-hidden rounded-xl sm:rounded-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-xl sm:text-2xl">
            <KeyRound className="w-6 h-6 text-blue-600 shrink-0" />
            Alterar Sua Senha
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-5 mt-4 w-full">
          <div className="space-y-2">
            <Label className="font-bold text-gray-700">Senha Atual</Label>
            <div className="relative">
              <Input
                type={showPassword ? "text" : "password"}
                required
                value={formData.current_password}
                onChange={(e) =>
                  setFormData({ ...formData, current_password: e.target.value })
                }
                className="pr-10 bg-white"
              />
              <button
                type="button"
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-blue-600 transition-colors"
                onClick={() => setShowPassword(!showPassword)}
              >
                {showPassword ? (
                  <EyeOff className="w-5 h-5" />
                ) : (
                  <Eye className="w-5 h-5" />
                )}
              </button>
            </div>
          </div>

          <div className="space-y-2">
            <Label className="font-bold text-gray-700">Nova Senha</Label>
            <Input
              type={showPassword ? "text" : "password"}
              required
              value={formData.new_password}
              onChange={(e) =>
                setFormData({ ...formData, new_password: e.target.value })
              }
              className="bg-white"
              placeholder="Mínimo 6 caracteres"
            />
          </div>

          <div className="space-y-2">
            <Label className="font-bold text-gray-700">
              Confirmar Nova Senha
            </Label>
            <Input
              type={showPassword ? "text" : "password"}
              required
              value={formData.confirm_password}
              onChange={(e) =>
                setFormData({ ...formData, confirm_password: e.target.value })
              }
              className="bg-white"
            />
          </div>

          <DialogFooter className="flex flex-col-reverse sm:flex-row gap-3 pt-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => setIsOpen(false)}
              className="w-full sm:w-auto"
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              disabled={loading}
              className="bg-blue-600 hover:bg-blue-700 shadow-md w-full sm:flex-1"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Salvando...
                </>
              ) : (
                "Salvar Nova Senha"
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
